/**
 * ParallaxBackground — layered planes at successive Z depths that scroll
 * with the camera to suggest 2.5D depth.
 *
 * Each layer is a camera-facing plane with a procedurally painted
 * DynamicTexture (gradient + silhouette strokes). On update, layers follow
 * the camera X by a `scrollFactor` from 0 (fully locked to world) to 1
 * (locked to camera). Typical factors:
 *   far sky      ≈ 0.95   — barely parallaxes; sky stays "out there"
 *   mid mountains≈ 0.6    — visible parallax between foreground + back
 *   near trees   ≈ 0.25   — obvious depth cue
 *
 * This is intentionally procedural so levels don't need texture files
 * shipped on disk. Level authors pass colors + silhouette spec; the
 * helper paints and caches.
 */
import { Scene } from "@babylonjs/core/scene";
import { Camera } from "@babylonjs/core/Cameras/camera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";

export type ParallaxPainter = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
) => void;

export interface ParallaxLayerSpec {
  /** Debug name. */
  name: string;
  /** World Z depth. Negative = further from the side-scroll camera. */
  z: number;
  /** Fraction of camera X the layer tracks. 0..1. */
  scrollFactor: number;
  /** World-space plane dimensions. Make these large enough for the FOV. */
  width: number;
  height: number;
  /** Vertical world position for the plane. */
  yOffset?: number;
  /** Base alpha for the plane material. */
  alpha?: number;
  /** Paint callback — draws into the layer's DynamicTexture. */
  paint: ParallaxPainter;
  /** When true, enable additive blending (nice for starfields / glow). */
  additive?: boolean;
  /** When true, light the plane as emissive-only (no shadow interaction). */
  emissiveOnly?: boolean;
  /**
   * Self-light multiplier for the painted texture (0-1). Defaults to 1 —
   * the emissive texture shows the paint at its authored colors; lower to
   * dim a layer without repainting.
   */
  emissiveLevel?: number;
}

/** Draw a smooth vertical gradient (top → bottom) between two hex colors. */
export function paintGradient(
  topHex: string,
  bottomHex: string,
): ParallaxPainter {
  return (ctx, w, h) => {
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, topHex);
    g.addColorStop(1, bottomHex);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  };
}

/** Draw a starfield scattered over a transparent plane. */
export function paintStarfield(density: number, seed = 0xC47E): ParallaxPainter {
  return (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    // tiny deterministic pseudo-random so reloads paint the same sky
    let s = seed >>> 0;
    const rand = (): number => {
      s = (s * 1664525 + 1013904223) >>> 0;
      // Use unsigned division so the result is always in [0, 1). The
      // `s & 0xffffffff` pattern is unsafe in JS because `&` reinterprets
      // its operands as 32-bit signed — a fully-high s becomes -1.
      return (s >>> 0) / 4294967296;
    };
    const count = Math.floor(w * h * density);
    for (let i = 0; i < count; i++) {
      const x = rand() * w;
      const y = rand() * h * 0.8;
      const r = 0.4 + rand() * 1.6;
      const a = 0.3 + rand() * 0.7;
      const tint = 0.7 + rand() * 0.3;
      ctx.fillStyle = `rgba(${Math.round(190 * tint + 40)},${Math.round(200 * tint + 40)},${Math.round(255 * tint)},${a})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  };
}

/**
 * Paint branching conifers with uneven crowns and open space between trunks.
 */
export function paintTreeSilhouette(
  hex: string,
  minHeight = 0.35,
  maxHeight = 0.75,
  count = 80,
  seed = 0xBADCAFE,
): ParallaxPainter {
  return (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    let s = seed >>> 0;
    const rand = (): number => {
      s = (s * 1103515245 + 12345) >>> 0;
      // Use unsigned division so the result is always in [0, 1). The
      // `s & 0xffffffff` pattern is unsafe in JS because `&` reinterprets
      // its operands as 32-bit signed — a fully-high s becomes -1.
      return (s >>> 0) / 4294967296;
    };
    ctx.fillStyle = hex;
    for (let i = 0; i < count; i++) {
      const cx = (i + rand() * 0.9) * (w / count);
      const topY = h - h * (minHeight + rand() * (maxHeight - minHeight));
      const halfW = w / count * (0.35 + rand() * 0.35);
      const treeHeight = h - topY;
      ctx.fillRect(cx - halfW * 0.07, topY, halfW * 0.14, treeHeight);
      // Overlapping, asymmetric branch tiers leave readable scalloped edges
      // instead of stretching a single triangle into a blurry vertical stripe.
      for (let tier = 0; tier < 7; tier++) {
        const t = tier / 7;
        const crownY = topY + treeHeight * t * 0.64;
        const spread = halfW * (0.22 + t * 0.78);
        const depth = treeHeight * (0.13 + rand() * 0.05);
        ctx.beginPath();
        ctx.moveTo(cx, crownY);
        ctx.lineTo(cx - spread, crownY + depth);
        ctx.lineTo(cx - spread * 0.3, crownY + depth * 0.84);
        ctx.lineTo(cx + spread * (0.7 + rand() * 0.3), crownY + depth * 1.05);
        ctx.closePath();
        ctx.fill();
      }
    }
  };
}

/** Repeating Victorian glasshouse bays, painted behind the physical play lane. */
export function paintGlasshouse(): ParallaxPainter {
  return (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    const bay = w / 12;
    const sill = h * 0.88;
    const spring = h * 0.42;
    ctx.lineWidth = Math.max(1.5, w / 700);
    ctx.strokeStyle = "#375256";
    for (let i = -1; i < 13; i++) {
      const x = i * bay;
      const glow = ctx.createLinearGradient(0, h * 0.12, 0, sill);
      glow.addColorStop(0, "rgba(106,151,148,0.16)");
      glow.addColorStop(1, "rgba(30,58,54,0.02)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.moveTo(x, sill);
      ctx.lineTo(x, spring);
      ctx.bezierCurveTo(x, h * 0.18, x + bay * 0.35, h * 0.12, x + bay / 2, h * 0.09);
      ctx.bezierCurveTo(x + bay * 0.65, h * 0.12, x + bay, h * 0.18, x + bay, spring);
      ctx.lineTo(x + bay, sill);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      for (let bar = 1; bar < 4; bar++) {
        const y = spring + (sill - spring) * bar / 4;
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + bay, y); ctx.stroke();
      }
      ctx.beginPath(); ctx.moveTo(x + bay / 2, h * 0.1); ctx.lineTo(x + bay / 2, sill); ctx.stroke();
      // A thin climbing vine gives each bay an organic interruption.
      ctx.strokeStyle = "#152f29";
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(x + 5, sill);
      ctx.bezierCurveTo(x + bay * 0.5, h * 0.7, x - bay * 0.25, h * 0.52, x + bay * 0.2, h * 0.3);
      ctx.stroke();
      ctx.strokeStyle = "#375256";
      ctx.lineWidth = Math.max(1.5, w / 700);
    }
  };
}

/**
 * Paint scattered clockwork/observatory silhouettes — gear circles with
 * tooth rings, simulating distant brass mechanisms seen across a
 * starlit sky. Used by the Clockwork Canopy finale instead of tree
 * silhouettes (which read as "wrong level / forest" per user feedback).
 */
export function paintClockworkSilhouette(
  hex: string,
  count = 14,
  seed = 0xC70C1E,
): ParallaxPainter {
  return (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    let s = seed >>> 0;
    const rand = (): number => {
      s = (s * 1103515245 + 12345) >>> 0;
      return (s >>> 0) / 4294967296;
    };
    ctx.strokeStyle = hex;
    ctx.lineWidth = 2;
    for (let i = 0; i < count; i++) {
      const cx = (i + rand() * 0.6) * (w / count);
      const cy = h - h * (0.35 + rand() * 0.5);
      const r = 12 + rand() * 28;
      // Open rings and spokes, rather than solid scalloped discs that
      // read as clouds. Compensate for this layer's wide world aspect.
      const point = (angle: number, radius: number): [number, number] =>
        [cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius * 1.8];
      ctx.beginPath();
      for (let t = 0; t <= 96; t++) {
        const radius = r * (t % 8 < 4 ? 1 : 1.12);
        const [x, y] = point(t / 96 * Math.PI * 2, radius);
        if (t === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath(); ctx.stroke();
      ctx.beginPath(); ctx.ellipse(cx, cy, r * 0.78, r * 1.8 * 0.78, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.ellipse(cx, cy, r * 0.14, r * 1.8 * 0.14, 0, 0, Math.PI * 2); ctx.stroke();
      for (let spoke = 0; spoke < 6; spoke++) {
        const angle = spoke / 6 * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(...point(angle, r * 0.14));
        ctx.lineTo(...point(angle, r * 0.78));
        ctx.stroke();
      }
    }
  };
}

/** Paint a soft haze band (horizontal gradient) for foreground fog. */
export function paintHazeBand(hex: string, alpha = 0.4): ParallaxPainter {
  return (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, `rgba(0,0,0,0)`);
    g.addColorStop(0.5, hex);
    g.addColorStop(1, `rgba(0,0,0,0)`);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 1;
  };
}

export class ParallaxBackground {
  private scene: Scene;
  private camera: Camera;
  private layers: Array<{ mesh: Mesh; spec: ParallaxLayerSpec }> = [];
  private disposed = false;

  constructor(scene: Scene, camera: Camera, specs: ParallaxLayerSpec[]) {
    this.scene = scene;
    this.camera = camera;
    for (const spec of specs) {
      this.addLayer(spec);
    }
  }

  private addLayer(spec: ParallaxLayerSpec): void {
    const plane = MeshBuilder.CreatePlane(
      `parallax_${spec.name}`,
      { width: spec.width, height: spec.height },
      this.scene,
    );
    plane.position = new Vector3(0, spec.yOffset ?? 0, spec.z);
    plane.isPickable = false;
    plane.checkCollisions = false;
    plane.receiveShadows = false;
    plane.renderingGroupId = 0; // draw before gameplay
    // Lock the plane facing the side-scroll camera (along +Z toward origin).
    plane.rotation = new Vector3(0, 0, 0);

    // Paint texture.
    // Wide architectural silhouettes need enough horizontal pixels to avoid
    // smearing branches and mullions over many world units.
    const texW = spec.width > 100 ? 2048 : 1024;
    const texH = 512;
    const dt = new DynamicTexture(
      `parallax_${spec.name}_tex`,
      { width: texW, height: texH },
      this.scene,
      false,
    );
    const ctx = dt.getContext() as unknown as CanvasRenderingContext2D;
    spec.paint(ctx, texW, texH);
    // Canvas y=0 is the top; Babylon plane UVs start at the bottom.
    // Without inversion the tree crowns and gothic arches hang upside down.
    dt.update(true);
    dt.hasAlpha = true;
    dt.wrapU = Texture.CLAMP_ADDRESSMODE;
    dt.wrapV = Texture.CLAMP_ADDRESSMODE;

    const mat = new StandardMaterial(`parallax_${spec.name}_mat`, this.scene);
    mat.diffuseTexture = dt;
    mat.opacityTexture = spec.emissiveOnly ? dt : null;
    // Q-039 black-cutout root cause, refined 2026-07-22 by live material
    // inspection: with `disableLighting = true` StandardMaterial's lit
    // diffuse term is zero and `emissiveColor` is a FLAT additive term —
    // it does NOT sample the diffuse texture. Painted layers therefore
    // rendered as flat silhouettes of their texture's alpha: flat BLACK
    // with the old black emissive (the "cutout" look), flat GRAY with any
    // non-black emissive (screenshot-verified to wash L1 white under
    // exposure/bloom). The paint only reaches the screen through an
    // EMISSIVE TEXTURE: set `emissiveTexture = dt` on every layer so the
    // fragment is paint.rgb × emissiveColor. The paints are authored at
    // dusk luminance, so full-white emissive shows them as authored;
    // `emissiveLevel` stays as a per-layer dim/lift knob.
    mat.emissiveTexture = dt;
    const level = spec.emissiveLevel ?? 1;
    mat.emissiveColor = new Color3(level, level, level);
    mat.diffuseColor = new Color3(0, 0, 0);
    mat.specularColor = Color3.Black();
    mat.disableLighting = true;
    // Backdrop layers are exempt from scene fog: the paints already encode
    // their own aerial perspective, and scene fog accumulating across a
    // multi-deep transparent layer stack (then lifted by exposure/bloom)
    // was screenshot-verified 2026-07-22 to wash L1's whole backdrop to
    // white glare. Scene fog still depth-cues real level geometry.
    mat.fogEnabled = false;
    mat.backFaceCulling = false;
    mat.alpha = spec.alpha ?? 1;
    if (spec.additive) {
      mat.useAlphaFromDiffuseTexture = true;
      // Additive blending at the material level
      mat.alphaMode = 1; // ADD
    } else {
      mat.useAlphaFromDiffuseTexture = true;
    }
    plane.material = mat;

    this.layers.push({ mesh: plane, spec });
  }

  /** Call each frame (or register in a scene.registerBeforeRender). */
  update(): void {
    if (this.disposed) return;
    const camX = this.camera.position.x;
    const camY = this.camera.position.y;
    for (const { mesh, spec } of this.layers) {
      // Track camera X at the layer's scrollFactor so nearer layers shift
      // more against distant ones.
      mesh.position.x = camX * spec.scrollFactor;
      // Track camera Y at a very low factor so a jump up doesn't reveal
      // the plane's edge. 0.05 is enough to hide the seam without feeling
      // like the sky slides with the player.
      mesh.position.y = (spec.yOffset ?? 0) + (camY - 1.2) * 0.05;
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const { mesh } of this.layers) {
      const mat = mesh.material as StandardMaterial | null;
      if (mat?.diffuseTexture) mat.diffuseTexture.dispose();
      mat?.dispose();
      mesh.dispose();
    }
    this.layers.length = 0;
  }
}
